import type { MatrixPos, Priority, Task } from "./types.js";
import { instantToFloating } from "../zone.js";

/** 3×3 importance/urgency matrix math. Axes are 0..1; cells are indexed 0..2
 *  (column = urgency left→right, row = importance bottom→top). The discrete
 *  `priority` is derived from the DIAGONAL (bottom-left low → top-right high). */

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Which third of an axis a 0..1 value falls in (0, 1 or 2). 1 lands in the top cell. */
export function matrixIndexOf(v: number): number {
  return Math.min(2, Math.floor(clamp01(v) * 3));
}

/** Center of a cell, e.g. column 0 → 1/6. */
export function matrixCellCenter(index: number): number {
  return (index * 2 + 1) / 6;
}

/** Cell-center position for a (column, row) tap. */
export function matrixPosForCell(col: number, row: number): MatrixPos {
  return { urgency: matrixCellCenter(col), importance: matrixCellCenter(row) };
}

const ROW_PRIORITY: Priority[] = ["low", "medium", "high"];

/** Diagonal score 0..1 of a position (bottom-left low/low = 0 → top-right
 *  high/high = 1). Drives BOTH the green→red matrix color and the derived
 *  discrete priority, so the two always agree. */
export function matrixScore(pos: MatrixPos): number {
  return clamp01((clamp01(pos.urgency) + clamp01(pos.importance)) / 2);
}

/** Discrete priority DERIVED from a placed position's diagonal — bottom-left
 *  → low, top-right → high (matches the matrix color gradient). */
export function priorityFromMatrix(pos: MatrixPos): Priority {
  return ROW_PRIORITY[matrixIndexOf(matrixScore(pos))]!;
}

/** Discrete urgency level from the urgency third of a stored position; a task
 *  never placed in the matrix has no urgency ("none"). */
export function urgencyFromMatrix(pos: MatrixPos | null): Priority {
  return pos ? ROW_PRIORITY[matrixIndexOf(pos.urgency)]! : "none";
}

/** Importance row (0..2) a discrete priority sits in; null for "none". */
export function matrixRowForPriority(priority: Priority): number | null {
  const row = ROW_PRIORITY.indexOf(priority);
  return row === -1 ? null : row;
}

/** Where to show the dot for a task: its stored position, or — for a task that
 *  has a discrete priority but was never placed in the matrix — the center of
 *  that priority's row at neutral urgency. */
export function matrixDisplayPos(pos: MatrixPos | null, priority: Priority): MatrixPos | null {
  if (pos) return { urgency: clamp01(pos.urgency), importance: clamp01(pos.importance) };
  const row = matrixRowForPriority(priority);
  return row === null ? null : { urgency: 0.5, importance: matrixCellCenter(row) };
}

/** Re-anchor a stored position after the user picks a discrete priority
 *  elsewhere (flag rail): keep the urgency, snap importance to the row center.
 *  Never INVENTS a position — an unplaced task stays unplaced (the matrix shows
 *  a ghost dot for it), so a flag tap can't silently matrix-place a whole
 *  series. Returns null when priority is "none" (placement cleared). */
export function matrixPosForPriorityChange(priority: Priority, current: MatrixPos | null): MatrixPos | null {
  const row = matrixRowForPriority(priority);
  if (row === null || current === null) return null;
  if (matrixIndexOf(current.importance) === row) return current;
  return { urgency: current.urgency, importance: matrixCellCenter(row) };
}

export function sameMatrixPos(a: MatrixPos | null, b: MatrixPos | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.urgency === b.urgency && a.importance === b.importance && !!a.urgencyAuto === !!b.urgencyAuto;
}

/** Column (0=low … 2=high) a discrete urgency level maps to. */
const URGENCY_COL: Record<"low" | "medium" | "high", number> = { low: 0, medium: 1, high: 2 };

/**
 * How the urgency axis is auto-populated from a deadline for a task the user
 * has NOT manually placed in the matrix. When `enabled`, urgency is derived
 * from the whole-day distance to the deadline: within `highWithinDays` → high,
 * within `mediumWithinDays` → medium, beyond that → low. (`0` days = the
 * deadline is today; negative = overdue → high.)
 */
export interface DeadlineUrgencyConfig {
  enabled: boolean;
  highWithinDays: number;
  mediumWithinDays: number;
}

export const DEADLINE_URGENCY_DEFAULT: DeadlineUrgencyConfig = {
  enabled: true,
  highWithinDays: 1,
  mediumWithinDays: 3,
};

/** Whole calendar-days from now's day to the deadline's day, measured in `tz`
 *  (0 = same day, negative = the deadline is in the past / overdue). */
export function daysUntilDeadline(deadlineIso: string, now: Date, tz: string): number {
  const due = instantToFloating(deadlineIso, tz);
  const cur = instantToFloating(now.toISOString(), tz);
  // Floating dates hold the wall-clock in `tz` in their UTC fields, so flooring
  // to the UTC calendar day yields the local day in `tz`.
  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const curDay = Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth(), cur.getUTCDate());
  return Math.round((dueDay - curDay) / 86_400_000);
}

/** Discrete urgency level derived from a deadline's day-distance; "none" when
 *  derivation is disabled or there is no deadline. */
export function deadlineUrgencyLevel(
  deadlineIso: string | null,
  now: Date,
  tz: string,
  cfg: DeadlineUrgencyConfig,
): Priority {
  if (!cfg.enabled || !deadlineIso) return "none";
  const days = daysUntilDeadline(deadlineIso, now, tz);
  if (days <= cfg.highWithinDays) return "high";
  if (days <= cfg.mediumWithinDays) return "medium";
  return "low";
}

/** The urgency axis VALUE (0..1, a column center) a deadline implies, or null
 *  when derivation is off / there is no deadline. */
export function deadlineUrgencyValue(
  deadlineIso: string | null,
  now: Date,
  tz: string,
  cfg: DeadlineUrgencyConfig,
): number | null {
  const level = deadlineUrgencyLevel(deadlineIso, now, tz, cfg);
  return level === "none" ? null : matrixCellCenter(URGENCY_COL[level]);
}

/** Whether the urgency axis is auto (deadline-driven) for a task: an unplaced
 *  task is always auto; a placed one follows its `urgencyAuto` flag. */
export function isUrgencyAuto(pos: MatrixPos | null): boolean {
  return !pos || !!pos.urgencyAuto;
}

/** Effective urgency LEVEL of a task: the manually-placed urgency third when
 *  urgency is manual, else the level DERIVED from its deadline. A placed task
 *  with auto urgency but no deadline falls back to its stored snapshot third;
 *  an unplaced task with no deadline has no urgency ("none"). */
export function effectiveUrgencyLevel(
  pos: MatrixPos | null,
  deadlineIso: string | null,
  now: Date,
  tz: string,
  cfg: DeadlineUrgencyConfig,
): Priority {
  if (pos && !pos.urgencyAuto) return urgencyFromMatrix(pos);
  const derived = deadlineUrgencyLevel(deadlineIso, now, tz, cfg);
  if (derived !== "none") return derived;
  return pos ? urgencyFromMatrix(pos) : "none";
}

/** Display position incorporating derived urgency. A placed task with auto
 *  urgency shows its dot at the DERIVED urgency column (importance unchanged); a
 *  manual placement shows its stored spot. A flag-only (unplaced) task with a
 *  deadline ghosts at its derived urgency column but still needs an importance
 *  row (a discrete priority) to show a dot. The returned pos carries the
 *  resolved `urgencyAuto` so the widget can render the auto state. */
export function effectiveMatrixDisplayPos(
  pos: MatrixPos | null,
  priority: Priority,
  deadlineIso: string | null,
  now: Date,
  tz: string,
  cfg: DeadlineUrgencyConfig,
): MatrixPos | null {
  const derivedVal = deadlineUrgencyValue(deadlineIso, now, tz, cfg);
  if (pos) {
    const auto = !!pos.urgencyAuto;
    const urgency = auto && derivedVal != null ? derivedVal : clamp01(pos.urgency);
    return { urgency, importance: clamp01(pos.importance), urgencyAuto: auto };
  }
  const row = matrixRowForPriority(priority);
  if (row === null) return null;
  return { urgency: derivedVal ?? 0.5, importance: matrixCellCenter(row) };
}

/** Which entries the matrix system covers (Settings → Tasks: off / tasks /
 *  tasks + events). */
export type MatrixScope = "off" | "tasks" | "tasks_events";

/** Whether the matrix system covers this kind of entry under `scope`. */
export function matrixEnabledFor(scope: MatrixScope, isEvent: boolean): boolean {
  if (scope === "off") return false;
  if (scope === "tasks_events") return true;
  return !isEvent;
}

/** Sort rank for a discrete priority/urgency level (higher = more pressing). */
export const PRIORITY_RANK: Record<Priority, number> = { high: 3, medium: 2, low: 1, none: 0 };

/** Time + settings context for the LIVE (deadline-derived) priority/urgency
 *  reads — shared by the sidebar filters, the list sorts/columns and the block
 *  color resolver so they can never drift apart. */
export interface EffectivePriorityCtx {
  now: Date;
  defaultTz: string;
  config: DeadlineUrgencyConfig;
  matrixScope: MatrixScope;
}

/** The task fields the live (matrix-derived) priority/urgency depend on. */
export type MatrixPriorityTask = Pick<Task, "priority" | "matrix_pos" | "deadline_at" | "due_at" | "tz" | "is_event">;

/**
 * A task's LIVE priority: when the matrix system covers it and the dot is
 * placed, the diagonal of the effective (deadline-derived) position — so it
 * updates as the deadline nears WITHOUT a re-save. Otherwise the stored flag.
 */
export function effectivePriorityOf(task: MatrixPriorityTask, ctx: EffectivePriorityCtx): Priority {
  if (!task.matrix_pos || !matrixEnabledFor(ctx.matrixScope, task.is_event)) return task.priority;
  return effectivePriorityLevel(task.matrix_pos, task.deadline_at ?? task.due_at, ctx.now, task.tz ?? ctx.defaultTz, ctx.config);
}

/** A task's LIVE urgency level (see {@link effectiveUrgencyLevel}): the manual
 *  placement's third, else the deadline-derived level. Not scope-gated —
 *  deadline urgency has its own enable switch in `ctx.config`. */
export function effectiveUrgencyOf(task: MatrixPriorityTask, ctx: EffectivePriorityCtx): Priority {
  return effectiveUrgencyLevel(task.matrix_pos, task.deadline_at ?? task.due_at, ctx.now, task.tz ?? ctx.defaultTz, ctx.config);
}

/**
 * Patch (if any) for ONE stored task when the matrix scope setting changes, so
 * the priority the task showed under the OLD mode survives into the NEW one.
 * An unplaced task needs nothing — the placed-gate fallback in
 * {@link effectivePriorityOf} keeps its flag governing in both modes.
 *
 * Leaving scope (matrix → flags): the stored flag becomes the priority source
 * again, so the LIVE diagonal (incl. deadline-derived urgency) is snapshotted
 * into `priority`. An auto-urgency placement also materializes the derived
 * urgency column into the stored position so placement and snapshot agree —
 * a later switch back then reads as "unchanged" rather than a manual re-flag.
 *
 * Entering scope (flags → matrix): a placed task whose stored diagonal
 * disagrees with its flag was re-flagged while the matrix was off. The flag is
 * the user's latest word, so the stale placement is CLEARED — never reshaped;
 * the matrix must not invent a position — letting the flag keep governing via
 * the placed-gate fallback (the widget ghosts it at the flag's row). Deadline
 * drift can't false-positive this: the check compares the STORED diagonal,
 * which the leave-scope materialization keeps in sync with the flag.
 */
export function matrixScopeSwitchPatch(
  task: MatrixPriorityTask,
  oldScope: MatrixScope,
  newScope: MatrixScope,
  now: Date,
  defaultTz: string,
  cfg: DeadlineUrgencyConfig,
): { priority?: Priority; matrix_pos?: MatrixPos | null } | null {
  const pos = task.matrix_pos;
  if (!pos) return null;
  const wasCovered = matrixEnabledFor(oldScope, task.is_event);
  const isCovered = matrixEnabledFor(newScope, task.is_event);
  if (wasCovered === isCovered) return null;
  if (wasCovered) {
    const tz = task.tz ?? defaultTz;
    const deadline = task.deadline_at ?? task.due_at;
    const patch: { priority?: Priority; matrix_pos?: MatrixPos } = {};
    const live = effectivePriorityLevel(pos, deadline, now, tz, cfg);
    if (live !== task.priority) patch.priority = live;
    if (pos.urgencyAuto) {
      const derived = deadlineUrgencyValue(deadline, now, tz, cfg);
      if (derived != null && derived !== pos.urgency) patch.matrix_pos = { ...pos, urgency: derived };
    }
    return patch.priority !== undefined || patch.matrix_pos !== undefined ? patch : null;
  }
  return priorityFromMatrix(pos) === task.priority ? null : { matrix_pos: null };
}

/** The discrete priority a task's EFFECTIVE matrix position implies (diagonal),
 *  incorporating deadline-derived urgency — "none" when unplaced. This is what
 *  the form persists to `priority` so colors/flags/filters follow the matrix. */
export function effectivePriorityLevel(
  pos: MatrixPos | null,
  deadlineIso: string | null,
  now: Date,
  tz: string,
  cfg: DeadlineUrgencyConfig,
): Priority {
  if (!pos) return "none";
  const display = effectiveMatrixDisplayPos(pos, "none", deadlineIso, now, tz, cfg);
  return display ? priorityFromMatrix(display) : "none";
}
